import { faEye, faTrash } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { Icon } from '../../../../components/base/Icon';
import { Text } from '../../../../components/base/Text';
import { Card } from '../../../../components/display/Card';
import { Input } from '../../../../components/forms/Input';
import { useThemeColors } from '../../../../providers/ThemeProvider';
import { cn } from '../../../../utils/cn';
import type { ConditionalVisibility, FieldConfiguration } from '../../../types';

interface ConditionalVisibilityConfiguratorProps {
	field: FieldConfiguration;
	onUpdateField: (updates: Partial<FieldConfiguration>) => void;
}

// Helper to safely convert unknown value to string for display
const valueToDisplayString = (value: unknown): string => {
	if (value == null) {
		return '';
	}

	if (typeof value === 'string') {
		return value;
	}

	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}

	return JSON.stringify(value);
};

// Condition options
const conditionOptions: Array<{
	value: ConditionalVisibility['condition'];
	label: string;
	requiresValue: boolean;
}> = [
	{ value: 'is_null', label: 'Is Null', requiresValue: false },
	{ value: 'is_not_null', label: 'Is Not Null', requiresValue: false },
	{ value: 'equals', label: 'Equals', requiresValue: true },
	{ value: 'not_equals', label: 'Not Equals', requiresValue: true },
];

export function ConditionalVisibilityConfigurator({
	field,
	onUpdateField,
}: ConditionalVisibilityConfiguratorProps): React.ReactElement {
	const colors = useThemeColors();

	const hasConditionalVisibility = field.conditionalVisibility != null;
	const currentCondition = conditionOptions.find(
		(c) => c.value === field.conditionalVisibility?.condition,
	);

	const updateConditionalVisibility = (
		updates: Partial<ConditionalVisibility>,
	): void => {
		onUpdateField({
			conditionalVisibility: {
				field: field.conditionalVisibility?.field ?? '',
				condition: field.conditionalVisibility?.condition ?? 'is_not_null',
				...updates,
			},
		});
	};

	const toggleConditionalVisibility = (): void => {
		if (hasConditionalVisibility) {
			// Remove conditional visibility
			onUpdateField({ conditionalVisibility: undefined });
		} else {
			// Add default conditional visibility
			onUpdateField({
				conditionalVisibility: {
					field: '',
					condition: 'is_not_null',
				},
			});
		}
	};

	return (
		<Card className="mb-4">
			<div className="p-4">
				<div className="flex items-center justify-between mb-3">
					<div className="flex items-center">
						<Icon
							icon={faEye}
							size="sm"
							className={`mr-2 ${colors.classes.textPrimary}`}
						/>
						<Text className="text-base font-semibold">
							Conditional Visibility
						</Text>
					</div>
					{hasConditionalVisibility && (
						<button
							onClick={toggleConditionalVisibility}
							className={cn(
								'p-1.5 rounded-md transition-colors',
								colors.classes.textDanger,
								colors.classes.bgPrimaryHover,
							)}
							title="Remove conditional visibility"
						>
							<Icon icon={faTrash} size="xs" />
						</button>
					)}
				</div>

				{!hasConditionalVisibility ? (
					<>
						<Text className="text-sm mb-3" color="secondary">
							Add a condition to show or hide this field based on data values
						</Text>
						<button
							onClick={toggleConditionalVisibility}
							className={cn(
								'w-full py-2 px-3 text-sm rounded-md border-2 transition-colors',
								colors.classes.bgPrimary,
								colors.classes.borderDefault,
								colors.classes.textSecondary,
								colors.classes.bgPrimaryHover,
							)}
						>
							Add Visibility Condition
						</button>
					</>
				) : (
					<>
						{/* Field Name */}
						<div className="mb-4">
							<Text className="text-sm font-medium mb-2">Field to Check</Text>
							<Input
								value={field.conditionalVisibility?.field ?? ''}
								onChange={(e) => {
									updateConditionalVisibility({ field: e.target.value });
								}}
								placeholder="e.g., status, isActive, type"
							/>
							<Text className="text-xs mt-1" color="secondary">
								The field name to evaluate for visibility
							</Text>
						</div>

						{/* Condition Selection */}
						<div className="mb-4">
							<Text className="text-sm font-medium mb-2">Condition</Text>
							<div className="grid grid-cols-2 gap-2">
								{conditionOptions.map((option) => (
									<button
										key={option.value}
										onClick={() => {
											updateConditionalVisibility({
												condition: option.value,
												// Clear value if switching to a condition that doesn't need it
												value: option.requiresValue
													? field.conditionalVisibility?.value
													: undefined,
											});
										}}
										className={cn(
											'py-2 px-3 text-xs rounded-md border-2 text-center transition-colors',
											field.conditionalVisibility?.condition === option.value
												? cn(
														colors.classes.controlSelected,
														colors.classes.borderPrimary,
														'font-semibold',
													)
												: cn(
														colors.classes.bgPrimary,
														colors.classes.borderDefault,
														colors.classes.textSecondary,
														'font-medium',
														colors.classes.bgPrimaryHover,
													),
										)}
									>
										{option.label}
									</button>
								))}
							</div>
						</div>

						{/* Value Input (for equals/not_equals) */}
						{currentCondition?.requiresValue === true && (
							<div className="mb-4">
								<Text className="text-sm font-medium mb-2">
									Comparison Value
								</Text>
								<Input
									value={valueToDisplayString(
										field.conditionalVisibility?.value,
									)}
									onChange={(e) => {
										updateConditionalVisibility({ value: e.target.value });
									}}
									placeholder="e.g., active, true, pending"
								/>
								<Text className="text-xs mt-1" color="secondary">
									The value to compare against. Field is visible when condition
									is met.
								</Text>
							</div>
						)}

						{/* Preview of condition */}
						<div
							className={cn(
								'p-3 rounded-md',
								colors.classes.bgSecondary,
								colors.classes.borderDefault,
								'border',
							)}
						>
							<Text className="text-xs font-medium mb-1">
								Visibility Rule Preview
							</Text>
							<Text className="text-xs" color="secondary">
								{field.conditionalVisibility?.field != null &&
								field.conditionalVisibility.field !== ''
									? `Show field when "${field.conditionalVisibility.field}" ${
											field.conditionalVisibility.condition === 'is_null'
												? 'is null'
												: field.conditionalVisibility.condition ===
													  'is_not_null'
													? 'is not null'
													: field.conditionalVisibility.condition === 'equals'
														? `equals "${valueToDisplayString(field.conditionalVisibility.value)}"`
														: `does not equal "${valueToDisplayString(field.conditionalVisibility.value)}"`
										}`
									: 'Configure a field name to see preview'}
							</Text>
						</div>
					</>
				)}
			</div>
		</Card>
	);
}
